import { useMemo } from 'react'
import { useRouteMatch } from 'react-router-dom'

import useAgent from 'hooks/use-agent'
import useAuth from 'hooks/use-auth'
import {
  arrayToObject,
  getRequestNode,
  getPendingRequests
} from 'components/kubernetes/access-control/utils'

export function useManageClusterAccess({
  assetId = '',
  poll = false,
  skipTeamRequests = true,
  skipRequestsToTeam = true,
  skipAllAssetsRequests = true
} = {}) {
  const match = useRouteMatch()
  const { currentTeam, currentTeamId, teams } = useAuth()
  const {
    params: { assetId: paramAssetId }
  } = match

  const currentAssetId = paramAssetId || assetId

  const { data: allClusterRequests, isLoading, hasError, doFetch } = useAgent(
    `/sharedAssets/requests/toMe?teamId=${currentTeamId}`,
    { assetToRequestMap: {}, idToTeamMapping: {} },
    {
      shouldCache: true,
      interval: poll && 15000,
      skipRequest: skipRequestsToTeam || teams.length === 0
    }
  )

  const currentClusterRequests = useMemo(
    () => allClusterRequests?.assetToRequestMap?.[currentAssetId] ?? [],
    [allClusterRequests?.assetToRequestMap, currentAssetId]
  )

  const pendingRequestsByAssetId = useMemo(() => {
    if (isLoading) return {}

    let pendingRequests = {}
    const allRequests = Object.assign(
      {},
      allClusterRequests?.assetToRequestMap ?? {}
    )
    // this is to filter the values ( all pending, approved and deleted request)
    // to only pending requests
    // if there isn't any pending request then omit the key == assetId
    Object.entries(allRequests).forEach(([key, value]) => {
      const convertedRequests = value.map(rawRequest =>
        getRequestNode({
          rawRequest,
          team: allClusterRequests?.idToTeamMapping?.[rawRequest.sharedTeam]
        })
      )
      const pendings = getPendingRequests({
        namespaces: convertedRequests
      })

      if (pendings.length > 0) {
        pendingRequests[key] = pendings
      }
    })

    return pendingRequests
  }, [allClusterRequests, isLoading])

  const {
    data: allTeamsRequests,
    isLoading: teamRequestsLoading,
    hasError: teamRequestsHasError,
    doFetch: doFetchTeamRequests
  } = useAgent(
    `/sharedAssets/requests?teamId=${currentTeamId}`,
    { assetToRequestMap: {}, idToTeamMapping: {} },
    {
      shouldCache: true,
      interval: poll && 15000,
      skipRequest: skipTeamRequests || teams.length === 0
    }
  )

  const {
    data: { myClusters, availableClusters },
    isLoading: isMyClusterLoading,
    hasError: isMyClusterHasError,
    doFetch: doFetchAllClusters
  } = useAgent(
    `/sharedAssets?teamId=${currentTeamId}`,
    { myClusters: [], availableClusters: [] },
    {
      shouldCache: true,
      skipRequest: skipAllAssetsRequests || teams.length === 0
    }
  )

  //////////////////////////////////////// GET ALL PENDING REQUESTS SENT BY ME
  const pendingRequestsFromMeByAssetId = useMemo(() => {
    if (teamRequestsLoading) return {}

    let pendingRequests = {}
    const allRequests = Object.assign(
      {},
      allTeamsRequests?.assetToRequestMap ?? {}
    )
    // this is to filter the values ( all pending, approved and deleted request)
    // to only pending requests
    // if there isn't any pending request then omit the key == assetId
    Object.entries(allRequests).forEach(([key, value]) => {
      const convertedRequests = value.map(rawRequest =>
        getRequestNode({
          rawRequest,
          team: allTeamsRequests?.idToTeamMapping?.[rawRequest.sharedTeam]
        })
      )
      const pendings = getPendingRequests({
        namespaces: convertedRequests
      }).filter(request => request.teamId === currentTeamId)

      if (pendings.length > 0) {
        pendingRequests[key] = pendings
      }
    })

    return pendingRequests
  }, [allTeamsRequests, teamRequestsLoading, currentTeamId])

  //////////////////////////////////////

  const { owningTeamId, currentClusterName, currentCluster } = useMemo(() => {
    if (isMyClusterLoading || !myClusters) {
      return {
        currentClusterName: currentAssetId
      }
    }

    const currentCluster =
      myClusters.filter(cluster => cluster.assetId === currentAssetId)?.[0] ??
      {}

    return {
      owningTeamId: currentCluster?.owningTeamId,
      currentClusterName: currentCluster?.clusterName,
      currentCluster
    }
  }, [myClusters, currentAssetId, isMyClusterLoading])

  const clusterName = currentClusterName ?? currentAssetId

  const teamAvailableNamespaces = useMemo(() => {
    if (isLoading || isMyClusterLoading) return []

    if (!currentCluster || !currentCluster.availableNamespaces) return []

    return currentCluster.availableNamespaces.map(namespace => ({
      status: 'SHAREABLE',
      name: namespace,
      teamId: currentCluster.owningTeamId,
      team: currentTeam,
      assetId: currentCluster.assetId,
      clusterName: currentCluster.clusterName,
      owningTeamId: currentCluster.owningTeamId
    }))
  }, [currentCluster, isLoading, currentTeam, isMyClusterLoading])

  const allTeamsDefinition = useMemo(() => {
    if (isLoading) return {}

    let teamsWithRequests = arrayToObject({
      data: currentClusterRequests,
      keyFn: item => item.sharedTeam,
      valueFn: item => ({
        identifier: item.sharedTeam
      })
    })

    const idToTeamMapping = skipRequestsToTeam
      ? allTeamsRequests?.idToTeamMapping
      : allClusterRequests?.idToTeamMapping ?? {}

    // here include all other teams
    if (Object.values(idToTeamMapping)?.length > 0) {
      Object.values(idToTeamMapping).forEach(team => {
        if (team.identifier !== currentTeamId) {
          teamsWithRequests[team.identifier] = {
            identifier: team.identifier,
            name: team.name,
            usersCount: team.usersCount ?? 0
          }
        }
      })
    }

    return teamsWithRequests
  }, [
    currentClusterRequests,
    allClusterRequests,
    allTeamsRequests,
    isLoading,
    currentTeamId,
    skipRequestsToTeam
  ])

  return {
    assetId: currentAssetId,
    allTeamsDefinition: allTeamsDefinition,
    pendingRequestsFromMeByAssetId: pendingRequestsFromMeByAssetId,
    allClusterRequests: currentClusterRequests,
    isLoading: isLoading || isMyClusterLoading,
    hasError: hasError || isMyClusterHasError,
    fetchRequestsToMe: doFetch,
    doFetchAllClusters: doFetchAllClusters,
    isMyClusterLoading,
    isMyClusterHasError,
    clusterName,
    teams,
    teamAvailableNamespaces,
    currentTeamId,
    owningTeamId,
    allTeamsRequests,
    teamRequestsLoading,
    teamRequestsHasError,
    doFetchTeamRequests,
    myCluster: currentCluster,
    availableClusters,
    myClusters,
    allRequestsByAssetId: allClusterRequests,
    pendingRequestsByAssetId
  }
}
